import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FaComments, FaUser, FaCalendar, FaEye, FaCheckCircle,
  FaFilter, FaChartBar
} from 'react-icons/fa';

export default function AdminRentalMessages() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, closed
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [convsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/rentals/conversations/all`),
        axios.get(`${API}/admin/rentals/stats`)
      ]);
      
      setConversations(convsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    // Status filter
    if (filter !== 'all' && conv.status !== filter) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        conv.product_name?.toLowerCase().includes(searchLower) ||
        conv.renter_name?.toLowerCase().includes(searchLower) ||
        conv.owner_name?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            Messages de Location - Admin
          </h1>
          <p className="text-gray-600">
            Vue d'ensemble de toutes les conversations de location sur la plateforme
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Locations</p>
                    <p className="text-3xl font-bold text-[#0A2540]">{stats.total_rentals}</p>
                  </div>
                  <FaCheckCircle className="text-4xl text-[#635BFF]" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Conversations</p>
                    <p className="text-3xl font-bold text-[#0A2540]">{stats.total_conversations}</p>
                  </div>
                  <FaComments className="text-4xl text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Messages Total</p>
                    <p className="text-3xl font-bold text-[#0A2540]">{stats.total_messages}</p>
                  </div>
                  <FaChartBar className="text-4xl text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Revenu Total</p>
                    <p className="text-3xl font-bold text-[#00D4FF]">{stats.total_revenue}€</p>
                  </div>
                  <FaCheckCircle className="text-4xl text-[#00D4FF]" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FaFilter className="text-[#0A2540]" />
              <h2 className="text-lg font-semibold text-[#0A2540]">Filtres</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Rechercher (produit, utilisateur...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les conversations</SelectItem>
                  <SelectItem value="active">Conversations actives</SelectItem>
                  <SelectItem value="closed">Conversations fermées</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                onClick={() => {
                  setFilter('all');
                  setSearchTerm('');
                }}
                variant="outline"
              >
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Conversations List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#0A2540]">
              Conversations ({filteredConversations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredConversations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Aucune conversation trouvée
              </div>
            ) : (
              <div className="space-y-4">
                {filteredConversations.map((conv) => (
                  <Card
                    key={conv.id}
                    className="hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/rentals/conversations/${conv.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <FaComments className="text-[#00D4FF]" />
                            <h3 className="font-bold text-[#0A2540]">
                              {conv.product_name}
                            </h3>
                            <Badge className={conv.status === 'active' ? 'bg-[#635BFF]' : 'bg-gray-500'}>
                              {conv.status}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-2">
                              <FaUser className="text-blue-500" />
                              <span>Locataire: <strong>{conv.renter_name}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaUser className="text-[#635BFF]" />
                              <span>Loueur: <strong>{conv.owner_name}</strong></span>
                            </div>
                          </div>

                          {conv.last_message_preview && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                              Dernier message: "{conv.last_message_preview}"
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <FaCalendar />
                              <span>
                                {new Date(conv.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaComments />
                              <span>
                                Messages non lus: {conv.unread_count_renter + conv.unread_count_owner}
                              </span>
                            </div>
                          </div>
                        </div>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/rentals/conversations/${conv.id}`);
                          }}
                        >
                          <FaEye className="mr-2" /> Voir
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
